import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { formatDistanceToNow, format } from "date-fns";
import api, { apiErr, fileUrl, videoEmbed } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MessageCircle, Trash2, Send, HandHeart, Pencil, X, Clock, Share2, Mail, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import MediaUploader from "./MediaUploader";
import CommentThread from "./CommentThread";
import { GrowTextarea, LinkFields, CtaButton, LinkedText } from "./DispatchFields";

const EMOJIS = ["❤️", "🙏", "✨"];

export default function DispatchCard({ dispatch, onChange, onDelete }) {
  const { user, can, org, config, isTeam } = useAuth();
  const isAdmin = user?.role === "admin" || (can && can("news"));
  const [d, setD] = useState(dispatch);
  const commentsOff = d.comments_enabled === false;
  const [showComments, setShowComments] = useState(true);
  const [comments, setComments] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: d.title, body: d.body, country: d.country || "", media: d.media || [], videos: d.videos || [], video_url: d.video_url || "", link_url: d.link_url || "", link_label: d.link_label || "", is_prayer: d.is_prayer, allow_comments: d.allow_comments ?? (org?.comments_enabled !== false) });
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [active, setActive] = useState(0);
  const touchX = useRef(0);
  const media = d.media || [];
  const activeIdx = Math.min(active, Math.max(0, media.length - 1));
  const goNext = () => setActive((i) => (media.length ? (i + 1) % media.length : 0));
  const goPrev = () => setActive((i) => (media.length ? (i - 1 + media.length) % media.length : 0));
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) (dx < 0 ? goNext() : goPrev());
  };
  const BODY_LIMIT = 280;
  const bodyStr = d.body || "";
  const isLong = bodyStr.length > BODY_LIMIT;
  const shownBody = expanded || !isLong ? bodyStr : bodyStr.slice(0, BODY_LIMIT).trimEnd() + "…";

  const react = async (emoji) => {
    try {
      const { data } = await api.post(`/dispatches/${d.id}/react`, { emoji });
      setD(data); onChange?.(data);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const loadComments = async () => {
    if (!showComments && comments.length === 0) {
      try { const { data } = await api.get(`/dispatches/${d.id}/comments`); setComments(data); }
      catch (e) { toast.error(apiErr(e)); }
    }
    setShowComments(!showComments);
  };

  // Comments are open by default under each dispatch to invite encouragement.
  useEffect(() => {
    if (d.comments_enabled === false) return;
    let active = true;
    (async () => {
      try { const { data } = await api.get(`/dispatches/${d.id}/comments`); if (active) setComments(data); }
      catch (e) { /* non-blocking */ }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d.id]);

  const del = async () => {
    try { await api.delete(`/dispatches/${d.id}`); onDelete?.(d.id); toast.success("Dispatch removed"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const share = async () => {
    const origin = typeof window !== "undefined" ? window.location.origin : "https://missioncompanion.app";
    const slug = org?.slug;
    const url = slug
      ? (org?.public_feed_enabled ? `${origin}/j/${slug}` : `${origin}/${slug}`)
      : origin;
    const tripName = config?.trip_name || org?.org_name || "our mission";
    const text = `${d.title} — follow ${org?.org_name || tripName}'s journey:`;
    if (navigator.share) {
      try { await navigator.share({ title: d.title, text, url }); return; }
      catch { /* cancelled/unsupported — fall through to copy */ }
    }
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      toast.success("Link copied — paste it anywhere!");
    } catch { toast.error("Couldn't share — copy the link from your browser."); }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/dispatches/${d.id}`, form);
      setD(data); onChange?.(data); setEditing(false);
      toast.success("Dispatch updated");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const pubDate = d.publish_at || d.created_at;
  const when = d.scheduled
    ? (pubDate ? format(new Date(pubDate), "MMM d, yyyy · h:mm a") : "")
    : (pubDate ? formatDistanceToNow(new Date(pubDate), { addSuffix: true }) : "");

  return (
    <article className="bg-white rounded-2xl overflow-hidden shadow-[0_4px_24px_rgba(44,44,62,0.04)] animate-fade-up" data-testid="dispatch-card">
      {media.length > 0 && (
        <div className="relative select-none flex items-center justify-center bg-neutral-100" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          <img src={fileUrl(media[activeIdx], 1200)} alt={d.title} className="w-full h-auto max-h-[80vh] object-contain" data-testid="dispatch-featured-image" />
          {d.country && (
            <span className="absolute top-3 left-3 text-xs font-medium bg-[var(--brand-navy)]/85 text-white px-3 py-1 rounded-full backdrop-blur">📍 {d.country}</span>
          )}
          {media.length > 1 && (
            <>
              <button data-testid="dispatch-prev-btn" onClick={goPrev} aria-label="Previous photo" className="absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-black/45 text-white flex items-center justify-center backdrop-blur hover:bg-black/65 transition-colors">
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button data-testid="dispatch-next-btn" onClick={goNext} aria-label="Next photo" className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-black/45 text-white flex items-center justify-center backdrop-blur hover:bg-black/65 transition-colors">
                <ChevronRight className="h-5 w-5" />
              </button>
              <span className="absolute bottom-3 right-3 text-xs font-medium bg-black/60 text-white px-2.5 py-1 rounded-full backdrop-blur" data-testid="dispatch-photo-count">{activeIdx + 1} / {media.length}</span>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {media.map((_, i) => (
                  <button key={i} data-testid={`dispatch-dot-${i}`} onClick={() => setActive(i)} aria-label={`Go to photo ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all ${i === activeIdx ? "w-5 bg-white" : "w-1.5 bg-white/50 hover:bg-white/80"}`} />
                ))}
              </div>
            </>
          )}
        </div>
      )}
      {media.length > 1 && (
        <div className="flex gap-2 overflow-x-auto px-4 pt-3 pb-1" data-testid="dispatch-gallery">
          {media.map((m, i) => (
            <button key={m + i} data-testid={`dispatch-thumb-${i}`} onClick={() => setActive(i)}
              className={`shrink-0 rounded-lg overflow-hidden ring-2 transition-all ${i === activeIdx ? "ring-[var(--tenant-accent)]" : "ring-transparent opacity-60 hover:opacity-100"}`}>
              <img src={fileUrl(m, 200)} alt="" className="h-16 w-16 object-cover" />
            </button>
          ))}
        </div>
      )}
      {d.videos?.length > 0 && (
        <div className="px-4 pt-3 space-y-2" data-testid="dispatch-videos">
          {d.videos.map((v, i) => (
            <UploadedVideo key={v + i} src={v} testid={`dispatch-uploaded-video-${i}`} />
          ))}
        </div>
      )}
      {(() => {
        const v = videoEmbed(d.video_url);
        if (!v) return null;
        if (v.type === "iframe") return <div className="aspect-video w-full bg-black"><iframe src={v.src} title="video" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-testid="dispatch-video" /></div>;
        if (v.type === "video") return <video src={v.src} controls className="w-full aspect-video bg-black" data-testid="dispatch-video" />;
        return <a href={v.src} target="_blank" rel="noreferrer" data-testid="dispatch-video-link" className="flex items-center gap-2 px-5 py-3 text-sm text-[var(--brand-navy)] font-medium border-b"><span>▶ Watch video</span></a>;
      })()}
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            {d.is_prayer && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold accent-text mb-1"><HandHeart className="h-3.5 w-3.5" /> Prayer request</span>
            )}
            {d.scheduled && (
              <span data-testid="dispatch-scheduled-badge" className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full mb-1 ml-0"><Clock className="h-3 w-3" /> Scheduled</span>
            )}
            <h3 className="font-heading font-bold text-xl text-[#2C2C3E] leading-snug">{d.title}</h3>
            <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
              <span>{d.author_name} · {when}</span>
              {isTeam && d.emailed && (
                <span data-testid="dispatch-emailed-badge" title="Also sent to partners by email" className="inline-flex items-center gap-1 text-[10px] font-semibold text-[var(--brand-navy)] bg-[var(--brand-navy)]/8 px-1.5 py-0.5 rounded-full"><Mail className="h-3 w-3" /> Emailed</span>
              )}
            </div>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2 shrink-0">
              <button data-testid="dispatch-edit-btn" onClick={() => { setForm({ title: d.title, body: d.body, country: d.country || "", media: d.media || [], videos: d.videos || [], video_url: d.video_url || "", link_url: d.link_url || "", link_label: d.link_label || "", is_prayer: d.is_prayer, allow_comments: d.allow_comments ?? (org?.comments_enabled !== false) }); setEditing(true); }} className="text-muted-foreground hover:text-[var(--brand-navy)] transition-colors"><Pencil className="h-4 w-4" /></button>
              <button data-testid="dispatch-delete-btn" onClick={del} className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="h-4 w-4" /></button>
            </div>
          )}
        </div>
        <LinkedText text={shownBody} className="text-[15px] text-[#2C2C3E]/90 mt-3 whitespace-pre-wrap leading-relaxed" testid="dispatch-body" />
        {isLong && (
          <button data-testid="dispatch-read-more" onClick={() => setExpanded(!expanded)} className="mt-1.5 text-sm font-semibold accent-text hover:opacity-80 transition-opacity">
            {expanded ? "Show less" : "Read more"}
          </button>
        )}
        <CtaButton url={d.link_url} label={d.link_label} />

        <div className="flex items-center gap-2 mt-4">
          {EMOJIS.map((em) => {
            const active = d.my_reactions?.includes(em);
            const count = d.reaction_counts?.[em] || 0;
            return (
              <button key={em} data-testid={`react-${em}`} onClick={() => react(em)}
                className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm border transition-transform hover:scale-110 active:scale-90 ${active ? "accent-border bg-[var(--tenant-accent)]/10" : "border-border"}`}>
                <span>{em}</span>{count > 0 && <span className="text-xs font-medium">{count}</span>}
              </button>
            );
          })}
          {commentsOff ? (
            <span data-testid="dispatch-comments-off" className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground"><MessageCircle className="h-3.5 w-3.5" /> Comments off</span>
          ) : (
            <button data-testid="dispatch-comment-toggle" onClick={loadComments} className="ml-auto flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[var(--brand-navy)] transition-colors">
              <MessageCircle className="h-4 w-4" /> {d.comment_count || 0}
            </button>
          )}
          <button data-testid="dispatch-share-btn" onClick={share} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[var(--brand-navy)] transition-colors">
            <Share2 className="h-4 w-4" /> Share
          </button>
        </div>

        {showComments && !commentsOff && (
          <div className="mt-4 border-t pt-4">
            <CommentThread basePath={`/dispatches/${d.id}`} userId={user?.id} isAdmin={isAdmin}
              comments={comments} setComments={setComments}
              onCountChange={(n) => setD({ ...d, comment_count: n })} placeholder="Leave an encouragement…" />
          </div>
        )}
      </div>

      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[92dvh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-[var(--brand-navy)]">Edit dispatch</DialogTitle><DialogDescription className="sr-only">Update this dispatch</DialogDescription></DialogHeader>
          <div className="space-y-4 mt-2">
            <Input data-testid="edit-title-input" placeholder="Title" className="rounded-xl h-11" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <GrowTextarea testid="edit-body-input" value={form.body} onChange={(v) => setForm((f) => ({ ...f, body: v }))} />
            <Input data-testid="edit-country-input" placeholder="Country" className="rounded-xl h-11" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
            <Input data-testid="edit-video-input" placeholder="Video URL (YouTube, Vimeo)" className="rounded-xl h-11" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} />
            <MediaUploader media={form.media} videos={form.videos} onChange={({ media, videos }) => setForm((f) => ({ ...f, media, videos }))} />
            <LinkFields form={form} setForm={setForm} prefix="edit" />
            <div className="flex items-center justify-between"><Label className="text-sm">Mark as prayer request</Label><Switch data-testid="edit-prayer-switch" checked={form.is_prayer} onCheckedChange={(v) => setForm({ ...form, is_prayer: v })} /></div>
            <div className="flex items-center justify-between"><Label className="text-sm">Allow comments</Label><Switch data-testid="edit-comments-switch" checked={form.allow_comments} onCheckedChange={(v) => setForm({ ...form, allow_comments: v })} /></div>
            <Button data-testid="edit-save-btn" disabled={saving} onClick={saveEdit} className="w-full rounded-full accent-bg text-white h-11 hover:opacity-90">{saving ? "Saving…" : "Save changes"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </article>
  );
}


function UploadedVideo({ src, testid }) {
  const [optimizing, setOptimizing] = useState(false);
  useEffect(() => {
    const m = String(src).match(/\/files\/([^/?]+)/);
    const id = m && m[1];
    if (!id) return;
    let stop = false, timer;
    const check = async () => {
      try {
        const { data } = await api.get(`/files/${id}/status`);
        if (stop) return;
        if (data.is_video && !data.processed) { setOptimizing(true); timer = setTimeout(check, 4000); }
        else setOptimizing(false);
      } catch { /* ignore — non-blocking hint */ }
    };
    check();
    return () => { stop = true; clearTimeout(timer); };
  }, [src]);
  return (
    <div className="relative">
      <video src={fileUrl(src)} controls playsInline className="w-full rounded-xl bg-black aspect-video" data-testid={testid} />
      {optimizing && (
        <div className="absolute top-2 left-2 flex items-center gap-1.5 rounded-full bg-black/65 text-white text-xs font-medium px-2.5 py-1 backdrop-blur pointer-events-none" data-testid="video-optimizing">
          <Loader2 className="h-3 w-3 animate-spin" /> Optimizing video…
        </div>
      )}
    </div>
  );
}
